/**
 * Shared class-code contract used by backend and frontend integrations.
 * Keeps normalization + validation rules in one place so admin payloads,
 * persistence, and search behavior stay aligned.
 */

import { dateKeyInTimeZone, normalizeTimeZone } from './calendarDay.js';

import { MATH_SUBJECTS } from './mathSubjects.js';
import { TEACHING_FORMATS, type TeachingFormat } from './pricing.js';
import type { SessionZoomLinkMode } from './sessionZoomLink.js';
import {
  isSessionZoomLinkMode,
  resolveSessionZoomLinkMode,
} from './sessionZoomLink.js';
import { validateAccessCodeLevels } from './utils/accessCodeLevels.js';
import { parseZoomAccount } from './zoomAccountOptions.js';

export const CLASS_CODE_MAX_LENGTH = 50;
/**
 * Cap on the raw (pre-normalization) code a client may submit. Larger than
 * CLASS_CODE_MAX_LENGTH so pastes carrying separators and invisible
 * characters still reach normalization instead of being rejected up front.
 */
export const CLASS_CODE_RAW_INPUT_MAX_LENGTH = 100;
export const CLASS_LABEL_MAX_LENGTH = 200;
export const CLASS_NOTES_MAX_LENGTH = 1000;
export const CLASS_TRIAL_INTAKE_NOTES_MAX_LENGTH = 1000;
export const SESSION_OVERRIDE_NOTES_MAX_LENGTH = 1000;
export const SKIPPED_OCCURRENCE_NOTES_MAX_LENGTH = 1000;
export const DEFAULT_CLASS_TIMEZONE = 'Asia/Singapore';
export const TRIAL_INTAKE_STATUSES = ['accepting', 'paused', 'closed'] as const;
export type TrialIntakeStatus = (typeof TRIAL_INTAKE_STATUSES)[number];

/** Matches wall-clock time as HH:MM in 24-hour form (00:00–23:59). */
export const HH_MM_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// --- Interfaces ---

export interface RecurringClassTiming {
  readonly scheduleType: 'recurring';
  readonly dayOfWeek: number;
  readonly time: string;
  readonly timezone: string;
  readonly oneTimeDate: null;
  readonly effectiveFrom: Date | null;
  /**
   * Class-timezone calendar date (`YYYY-MM-DD`) of the first session, or
   * `null` for "already running". Base occurrences before it do not exist —
   * a class entered ahead of its start produces no sessions (ledger or
   * calendar) until this date. Admin-set, unlike `effectiveFrom`.
   */
  readonly startDate: string | null;
}

export interface OneTimeClassTiming {
  readonly scheduleType: 'one-time';
  readonly dayOfWeek: null;
  readonly time: null;
  readonly timezone: string;
  readonly oneTimeDate: Date;
  readonly effectiveFrom: Date | null;
  readonly startDate: null;
}

export type ClassTimingData = RecurringClassTiming | OneTimeClassTiming;

export interface ValidationSuccess<T> {
  readonly valid: true;
  readonly data: T;
}

export interface ValidationFailure {
  readonly valid: false;
  readonly error: string;
}

export type ValidationResult<T> = ValidationSuccess<T> | ValidationFailure;

export interface ClassTimingInput {
  scheduleType?: string;
  dayOfWeek?: number | null;
  time?: string;
  timezone?: string;
  oneTimeDate?: Date | string;
  startDate?: string | null;
}

/**
 * Timing fields that decide occurrence identity. Changing any of them can
 * rewrite history, so they may only be written through
 * `classScheduleEditService` (which seals the past first). `effectiveFrom` is
 * excluded: it is the service's own bookkeeping, not an admin-editable field.
 */
export const TIMING_SEMANTIC_CLASS_TIMING_FIELDS = Object.freeze([
  'scheduleType',
  'dayOfWeek',
  'time',
  'timezone',
  'oneTimeDate',
  'startDate',
] as const);

export interface ValidateClassTimingOptions {
  now?: Date;
  allowPastOneTimeDate?: boolean;
}

export interface SessionOverrideInput {
  _id?: string;
  startsAt?: Date | string;
  timezone?: string | null;
  zoomJoinUrl?: string | null;
  /**
   * Explicit "where does this session's link come from" — see
   * `shared/sessionZoomLink.ts`. Omit and it is derived from `zoomJoinUrl`,
   * which is what a blank URL always meant.
   */
  zoomLinkMode?: string | null;
  notes?: string | null;
  active?: boolean;
  reliefTeacherUserId?: string | null;
  replacesDate?: string | null;
  participantIds?: string[] | null;
  movesParticipantsFromDate?: string | null;
}

export interface SessionOverrideData {
  readonly _id?: string;
  readonly startsAt: Date;
  readonly timezone: string;
  readonly zoomJoinUrl: string | null;
  /**
   * Where this session's link comes from — always stamped, so "reuse the class
   * link" and "a different link is coming" never look identical in storage.
   * Derived from `zoomJoinUrl` when the payload omits it, which preserves the
   * old meaning of a blank URL for rows written before the field existed.
   */
  readonly zoomLinkMode: SessionZoomLinkMode;
  readonly notes: string | null;
  readonly active: boolean;
  readonly reliefTeacherUserId: string | null;
  /**
   * Class-timezone calendar date (`YYYY-MM-DD`) of the base occurrence this
   * override stands in for, so a reschedule is one action instead of an
   * override plus a manual skip. `null` = purely additive (an extra session
   * that replaces nothing). Always explicit — never inferred from the data.
   * Whether the date actually names a base occurrence is checked by
   * `classScheduleEditService`, which knows the schedule; this contract only
   * validates the shape.
   */
  readonly replacesDate: string | null;
  readonly participantIds?: string[] | null;
  readonly movesParticipantsFromDate?: string | null;
}

export interface SkippedOccurrenceInput {
  _id?: string;
  date?: string;
  timezone?: string | null;
  notes?: string | null;
  active?: boolean;
}

export interface SkippedOccurrenceData {
  readonly _id?: string;
  readonly date: string;
  readonly timezone: string;
  readonly notes: string | null;
  readonly active: boolean;
}

export interface ValidateSessionOverrideOptions {
  index?: number;
}

export const CLASS_JOIN_CODE_PURPOSES = Object.freeze([
  'live_class',
  'assignment_group',
] as const);
export type ClassJoinCodePurpose = (typeof CLASS_JOIN_CODE_PURPOSES)[number];

export interface ClassCodeSearchTerm {
  readonly raw: string;
  readonly classCodeNormalized: string | null;
}

export interface ClassCodeAdminPayloadData {
  teachingFormat?: TeachingFormat;
  classCodeNormalized?: string;
  zoomJoinUrl?: string | null;
  purpose?: ClassJoinCodePurpose;
  joinEnabled?: boolean;
  visibleInTeacherClassCodes?: boolean;
  notes?: string | null;
  trialIntakeStatus?: TrialIntakeStatus;
  trialIntakeNotes?: string | null;
  expiresAt?: Date | null;
  classTiming?: ClassTimingData | null;
  sessionOverrides?: SessionOverrideData[];
  skippedOccurrences?: SkippedOccurrenceData[];
  mainTeacherUserId?: string | null;
  /**
   * Cohort the class teaches. `orgLevel`/`examLevel` are validated as a pair
   * (primary → PSLE, secondary → one of G1/G2/G3/IP/IB); clearing `orgLevel`
   * clears both. They scope which topics/nodes/questions a teacher can assign.
   * Both null = unset (the assign-homework picker prompts an admin to set them).
   */
  orgLevel?: string | null;
  examLevel?: string | null;
  /** Optional math-subject narrowing (E-Math/A-Math) for upper-sec classes. */
  mathSubjects?: string[];
}

export interface SanitizePayloadOptions {
  existingTeachingFormat?: TeachingFormat | null;
  mode?: 'create' | 'update';
  now?: Date;
  existingPurpose?: ClassJoinCodePurpose | null;
  /**
   * Stored `notes` (the Zoom-account field), for update payloads that omit
   * `notes`. Decides whether an empty Zoom URL is allowed (Personal account →
   * awaiting-teacher-link state instead of a required error).
   */
  existingNotes?: string | null;
}

// --- Helpers ---

function hasOwn(obj: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function isValidObjectId(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true; // null/empty = clear
  return typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value.trim());
}

function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function parseDate(value: unknown): Date | null {
  const date =
    value instanceof Date ? value : new Date(value as string | number);
  return isValidDate(date) ? date : null;
}

export function isValidLocalDateString(value: string): boolean {
  if (!YYYY_MM_DD_REGEX.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Timezone strings are stored on class-code data and never re-validated on
 * read, and browsers do not all ship the same ICU timezone set — mobile Safari
 * carries a smaller one than desktop Chrome. So a zone that formats fine on one
 * device throws `RangeError` inside `Intl.DateTimeFormat` on another, taking
 * the whole render down with it.
 *
 * Resolve to a zone the *current* runtime actually supports, falling back to
 * the default rather than throwing. Results are cached because callers format
 * repeatedly (per row, per render).
 */
export function resolveSafeTimezone(
  timezone: string | null | undefined
): string {
  const candidate =
    typeof timezone === 'string' && timezone.trim()
      ? timezone.trim()
      : DEFAULT_CLASS_TIMEZONE;

  return normalizeTimeZone(candidate) ? candidate : DEFAULT_CLASS_TIMEZONE;
}

export function getDateKeyInTimezone(
  date: Date,
  timezone: string | null | undefined = DEFAULT_CLASS_TIMEZONE
): string | null {
  if (!isValidDate(date)) {
    return null;
  }

  return dateKeyInTimeZone(date, resolveSafeTimezone(timezone));
}

export function isClassOccurrenceSkipped(
  startsAt: Date,
  skippedOccurrences: unknown,
  fallbackTimezone: string | null | undefined = DEFAULT_CLASS_TIMEZONE
): boolean {
  if (!isValidDate(startsAt) || !Array.isArray(skippedOccurrences)) {
    return false;
  }

  for (const entry of skippedOccurrences as SkippedOccurrenceInput[]) {
    if (!entry || typeof entry !== 'object' || entry.active === false) {
      continue;
    }
    if (typeof entry.date !== 'string' || !isValidLocalDateString(entry.date)) {
      continue;
    }
    const timezone =
      typeof entry.timezone === 'string' && entry.timezone.trim()
        ? entry.timezone.trim()
        : fallbackTimezone;
    if (getDateKeyInTimezone(startsAt, timezone) === entry.date.trim()) {
      return true;
    }
  }

  return false;
}

export function isHttpsUrl(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  try {
    const url = new URL(value);
    return url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Liberal by design: students receive codes via WhatsApp/whiteboards and type
 * them on IME keyboards, so inputs arrive with spaces, unicode dash variants,
 * fullwidth characters, and invisible copy-paste characters (zero-width
 * spaces, bidi marks) that trim() does not remove. NFKC folds fullwidth forms
 * to ASCII; everything outside the code alphabet is then dropped.
 */
export function normalizeClassCode(code: unknown): string | null {
  if (code == null || typeof code !== 'string') {
    return null;
  }
  const normalized = code
    .normalize('NFKC')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (normalized === '') {
    return null;
  }
  if (normalized.length > CLASS_CODE_MAX_LENGTH) {
    return null;
  }
  return normalized;
}

/**
 * Keystroke-level cleanup for class-code text inputs. Same folding as
 * normalizeClassCode but keeps `-` so a typed separator (S1MATHIB-WED) stays
 * visible while the user types; dash lookalikes (en/em dash, minus sign) fold
 * to `-` instead of vanishing. normalizeClassCode strips the dashes at submit.
 */
export function sanitizeClassCodeInput(value: unknown): string {
  if (value == null || typeof value !== 'string') {
    return '';
  }
  return value
    .normalize('NFKC')
    .toUpperCase()
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[^A-Z0-9-]/g, '');
}

export function normalizeClassLabel(label: unknown): string | null {
  if (label == null || typeof label !== 'string') {
    return null;
  }

  const trimmed = label.trim();
  if (trimmed === '') {
    return null;
  }

  return trimmed.replace(/\s+/g, ' ').toLowerCase();
}

export function normalizeClassCodeSearchTerm(
  search: unknown
): ClassCodeSearchTerm {
  const raw = search != null && typeof search === 'string' ? search.trim() : '';
  if (!raw) {
    return {
      raw: '',
      classCodeNormalized: null,
    };
  }
  return {
    raw,
    classCodeNormalized: normalizeClassCode(raw),
  };
}

/**
 * Note on `effectiveFrom`: it is always returned as `null` here, never read off
 * the input. It marks the instant a schedule became true and is stamped
 * server-side by `classScheduleEditService` after that service has sealed the
 * past — so a client cannot forge it, back-date it, or future-date it. Callers
 * that persist a validated timing object MUST route through that service, which
 * carries the stored value forward when the schedule itself did not change.
 *
 * `startDate` is its admin-facing sibling: a client-supplied class-timezone
 * calendar date meaning "no base occurrences before this". Unlike
 * `effectiveFrom` it may sit in the future — that is the whole point (a class
 * entered ahead of its first session) — and the occurrence engine clamps on
 * both independently, so neither can widen what the other suppressed.
 */
export function validateClassTiming(
  classTiming: unknown,
  options: ValidateClassTimingOptions = {}
): ValidationResult<ClassTimingData> {
  if (
    classTiming == null ||
    typeof classTiming !== 'object' ||
    Array.isArray(classTiming)
  ) {
    return {
      valid: false,
      error: 'classTiming must be an object',
    };
  }

  const ct = classTiming as ClassTimingInput;
  const now = isValidDate(options.now) ? options.now : new Date();
  const allowPastOneTimeDate = options.allowPastOneTimeDate === true;
  const { scheduleType, dayOfWeek, time, timezone, oneTimeDate, startDate } =
    ct;
  const normalizedTimezone =
    typeof timezone === 'string' && timezone.trim()
      ? timezone.trim()
      : DEFAULT_CLASS_TIMEZONE;

  if (!scheduleType || !['recurring', 'one-time'].includes(scheduleType)) {
    return {
      valid: false,
      error:
        'classTiming.scheduleType must be either "recurring" or "one-time"',
    };
  }

  if (scheduleType === 'recurring') {
    if (
      dayOfWeek === null ||
      dayOfWeek === undefined ||
      typeof dayOfWeek !== 'number'
    ) {
      return {
        valid: false,
        error:
          'classTiming.dayOfWeek is required for recurring schedules and must be a number (0-6)',
      };
    }

    if (dayOfWeek < 0 || dayOfWeek > 6) {
      return {
        valid: false,
        error:
          'classTiming.dayOfWeek must be between 0 (Sunday) and 6 (Saturday)',
      };
    }

    if (!time || typeof time !== 'string') {
      return {
        valid: false,
        error:
          'classTiming.time is required for recurring schedules and must be in HH:MM format',
      };
    }

    if (!HH_MM_24H_REGEX.test(time)) {
      return {
        valid: false,
        error:
          'classTiming.time must be in HH:MM format (24-hour, e.g., "14:30")',
      };
    }

    // Deliberately allowed to be in the past: an unrelated later edit resubmits
    // the whole timing object, and a started class's startDate is simply inert.
    let normalizedStartDate: string | null = null;
    if (startDate != null && startDate !== '') {
      if (
        typeof startDate !== 'string' ||
        !isValidLocalDateString(startDate.trim())
      ) {
        return {
          valid: false,
          error:
            'classTiming.startDate must be a valid calendar date in YYYY-MM-DD format',
        };
      }
      normalizedStartDate = startDate.trim();
    }

    return {
      valid: true,
      data: {
        scheduleType: 'recurring',
        dayOfWeek,
        time,
        timezone: normalizedTimezone,
        oneTimeDate: null,
        effectiveFrom: null,
        startDate: normalizedStartDate,
      },
    };
  }

  if (startDate != null && startDate !== '') {
    return {
      valid: false,
      error:
        'classTiming.startDate is only supported for recurring schedules (the one-time date already is the start)',
    };
  }

  if (!oneTimeDate) {
    return {
      valid: false,
      error: 'classTiming.oneTimeDate is required for one-time schedules',
    };
  }

  const oneTimeDateObj = parseDate(oneTimeDate);
  if (!oneTimeDateObj) {
    return {
      valid: false,
      error: 'classTiming.oneTimeDate must be a valid date',
    };
  }

  if (!allowPastOneTimeDate && oneTimeDateObj <= now) {
    return {
      valid: false,
      error: 'classTiming.oneTimeDate must be in the future',
    };
  }

  return {
    valid: true,
    data: {
      scheduleType: 'one-time',
      dayOfWeek: null,
      time: null,
      timezone: normalizedTimezone,
      oneTimeDate: oneTimeDateObj,
      effectiveFrom: null,
      startDate: null,
    },
  };
}

export function validateSessionOverride(
  sessionOverride: unknown,
  options: ValidateSessionOverrideOptions = {}
): ValidationResult<SessionOverrideData> {
  const index = Number.isInteger(options.index) ? options.index : 0;
  const fieldPrefix = `sessionOverrides[${index}]`;

  if (
    sessionOverride == null ||
    typeof sessionOverride !== 'object' ||
    Array.isArray(sessionOverride)
  ) {
    return {
      valid: false,
      error: `${fieldPrefix} must be an object`,
    };
  }

  const so = sessionOverride as SessionOverrideInput;

  const startsAt = parseDate(so.startsAt);
  if (!startsAt) {
    return {
      valid: false,
      error: `${fieldPrefix}.startsAt must be a valid date`,
    };
  }

  let zoomJoinUrl: string | null = null;
  if (so.zoomJoinUrl != null) {
    if (typeof so.zoomJoinUrl !== 'string') {
      return {
        valid: false,
        error: `${fieldPrefix}.zoomJoinUrl must be a string when provided`,
      };
    }
    const trimmedUrl = so.zoomJoinUrl.trim();
    if (trimmedUrl !== '') {
      if (!isHttpsUrl(trimmedUrl)) {
        return {
          valid: false,
          error: `${fieldPrefix}.zoomJoinUrl must be a valid HTTPS URL when provided`,
        };
      }
      zoomJoinUrl = trimmedUrl;
    }
  }

  // The link mode and the URL have to agree. Rejecting the contradictions
  // rather than quietly picking one keeps a stale or buggy client from
  // re-introducing the ambiguity this field exists to remove.
  if (
    so.zoomLinkMode != null &&
    so.zoomLinkMode !== '' &&
    !isSessionZoomLinkMode(so.zoomLinkMode)
  ) {
    return {
      valid: false,
      error: `${fieldPrefix}.zoomLinkMode must be one of: inherit, custom, pending`,
    };
  }
  const zoomLinkMode = resolveSessionZoomLinkMode({
    zoomJoinUrl,
    zoomLinkMode: so.zoomLinkMode ?? null,
  });
  if (zoomLinkMode === 'custom' && zoomJoinUrl == null) {
    return {
      valid: false,
      error: `${fieldPrefix}.zoomJoinUrl is required when zoomLinkMode is "custom" — use "pending" if the link is still to come`,
    };
  }
  if (zoomLinkMode !== 'custom' && zoomJoinUrl != null) {
    return {
      valid: false,
      error: `${fieldPrefix}.zoomJoinUrl must be empty when zoomLinkMode is "${zoomLinkMode}"`,
    };
  }

  let notes: string | null = null;
  if (so.notes != null) {
    if (typeof so.notes !== 'string') {
      return {
        valid: false,
        error: `${fieldPrefix}.notes must be a string when provided`,
      };
    }
    const trimmedNotes = so.notes.trim();
    if (trimmedNotes.length > SESSION_OVERRIDE_NOTES_MAX_LENGTH) {
      return {
        valid: false,
        error: `${fieldPrefix}.notes must be ${SESSION_OVERRIDE_NOTES_MAX_LENGTH} characters or less`,
      };
    }
    notes = trimmedNotes || null;
  }

  // The link is the meeting, and a Zoom meeting is hosted by exactly one
  // account — a session on the class's link runs on the class's account, so it
  // has no account of its own to store. Sanitized rather than rejected: a
  // legacy row carrying a stale account must still save cleanly, and every read
  // path derives the class's account for 'inherit' anyway.
  if (zoomLinkMode === 'inherit') {
    notes = null;
  }

  if (so.active !== undefined && typeof so.active !== 'boolean') {
    return {
      valid: false,
      error: `${fieldPrefix}.active must be a boolean when provided`,
    };
  }

  if (
    so.timezone !== undefined &&
    so.timezone !== null &&
    typeof so.timezone !== 'string'
  ) {
    return {
      valid: false,
      error: `${fieldPrefix}.timezone must be a string when provided`,
    };
  }

  const timezone =
    typeof so.timezone === 'string' && so.timezone.trim()
      ? so.timezone.trim()
      : DEFAULT_CLASS_TIMEZONE;

  let reliefTeacherUserId: string | null = null;
  if (so.reliefTeacherUserId != null && so.reliefTeacherUserId !== '') {
    const asString =
      typeof so.reliefTeacherUserId === 'string'
        ? so.reliefTeacherUserId
        : typeof (so.reliefTeacherUserId as { toString?: () => string })
              ?.toString === 'function'
          ? (so.reliefTeacherUserId as { toString: () => string }).toString()
          : '';
    if (!asString || !isValidObjectId(asString)) {
      return {
        valid: false,
        error: `${fieldPrefix}.reliefTeacherUserId must be a valid user ID`,
      };
    }
    reliefTeacherUserId = asString.trim();
  }

  let replacesDate: string | null = null;
  if (so.replacesDate != null && so.replacesDate !== '') {
    if (typeof so.replacesDate !== 'string') {
      return {
        valid: false,
        error: `${fieldPrefix}.replacesDate must be a string when provided`,
      };
    }
    const trimmedReplacesDate = so.replacesDate.trim();
    if (!isValidLocalDateString(trimmedReplacesDate)) {
      return {
        valid: false,
        error: `${fieldPrefix}.replacesDate must be a valid YYYY-MM-DD date`,
      };
    }
    replacesDate = trimmedReplacesDate;
  }

  const participantIds = so.participantIds == null ? null : so.participantIds;
  if (
    participantIds !== null &&
    (!Array.isArray(participantIds) ||
      !participantIds.length ||
      participantIds.length > 200 ||
      participantIds.some(
        id =>
          typeof id !== 'string' || !/^(?:enrollment:)?[a-f0-9]{24}$/i.test(id)
      ) ||
      new Set(participantIds).size !== participantIds.length)
  ) {
    return {
      valid: false,
      error: `${fieldPrefix}.participantIds must contain distinct student or enrollment participant IDs`,
    };
  }
  const movesParticipantsFromDate =
    so.movesParticipantsFromDate == null ? null : so.movesParticipantsFromDate;
  if (
    movesParticipantsFromDate !== null &&
    (typeof movesParticipantsFromDate !== 'string' ||
      !isValidLocalDateString(movesParticipantsFromDate) ||
      !participantIds)
  ) {
    return {
      valid: false,
      error: `${fieldPrefix}.movesParticipantsFromDate requires selected students and a valid date`,
    };
  }
  if (participantIds && replacesDate)
    return {
      valid: false,
      error: `${fieldPrefix}: use movesParticipantsFromDate to move selected students`,
    };

  let idPassthrough: string | undefined;
  if (so._id != null && so._id !== '') {
    // Accept both string IDs (from JSON payloads) and ObjectId-like objects
    // (from Mongoose's pre-validate hook). Mongoose auto-assigns `_id` as an
    // ObjectId instance when the subdoc schema uses `_id: true`.
    const asString =
      typeof so._id === 'string'
        ? so._id
        : typeof (so._id as { toString?: () => string })?.toString ===
            'function'
          ? (so._id as { toString: () => string }).toString()
          : '';
    if (!asString || !isValidObjectId(asString)) {
      return {
        valid: false,
        error: `${fieldPrefix}._id must be a valid ObjectId when provided`,
      };
    }
    idPassthrough = asString.trim();
  }

  const data: SessionOverrideData = {
    ...(idPassthrough !== undefined && { _id: idPassthrough }),
    startsAt,
    timezone,
    zoomJoinUrl,
    zoomLinkMode,
    notes,
    active: so.active === undefined ? true : Boolean(so.active),
    reliefTeacherUserId,
    replacesDate,
    participantIds,
    movesParticipantsFromDate,
  };

  return { valid: true, data };
}

export function validateSessionOverrides(
  sessionOverrides: unknown
): ValidationResult<SessionOverrideData[]> {
  if (!Array.isArray(sessionOverrides)) {
    return {
      valid: false,
      error: 'sessionOverrides must be an array',
    };
  }

  const normalized: SessionOverrideData[] = [];
  for (let i = 0; i < sessionOverrides.length; i += 1) {
    const result = validateSessionOverride(sessionOverrides[i], { index: i });
    if (!result.valid) {
      return result;
    }
    normalized.push(result.data);
  }

  return {
    valid: true,
    data: normalized,
  };
}

export function validateSkippedOccurrence(
  skippedOccurrence: unknown,
  options: ValidateSessionOverrideOptions = {}
): ValidationResult<SkippedOccurrenceData> {
  const index = Number.isInteger(options.index) ? options.index : 0;
  const fieldPrefix = `skippedOccurrences[${index}]`;

  if (
    skippedOccurrence == null ||
    typeof skippedOccurrence !== 'object' ||
    Array.isArray(skippedOccurrence)
  ) {
    return {
      valid: false,
      error: `${fieldPrefix} must be an object`,
    };
  }

  const so = skippedOccurrence as SkippedOccurrenceInput;

  if (typeof so.date !== 'string' || !isValidLocalDateString(so.date.trim())) {
    return {
      valid: false,
      error: `${fieldPrefix}.date must be a valid YYYY-MM-DD date`,
    };
  }

  let notes: string | null = null;
  if (so.notes != null) {
    if (typeof so.notes !== 'string') {
      return {
        valid: false,
        error: `${fieldPrefix}.notes must be a string when provided`,
      };
    }
    const trimmedNotes = so.notes.trim();
    if (trimmedNotes.length > SKIPPED_OCCURRENCE_NOTES_MAX_LENGTH) {
      return {
        valid: false,
        error: `${fieldPrefix}.notes must be ${SKIPPED_OCCURRENCE_NOTES_MAX_LENGTH} characters or less`,
      };
    }
    notes = trimmedNotes || null;
  }

  if (so.active !== undefined && typeof so.active !== 'boolean') {
    return {
      valid: false,
      error: `${fieldPrefix}.active must be a boolean when provided`,
    };
  }

  if (
    so.timezone !== undefined &&
    so.timezone !== null &&
    typeof so.timezone !== 'string'
  ) {
    return {
      valid: false,
      error: `${fieldPrefix}.timezone must be a string when provided`,
    };
  }

  const timezone =
    typeof so.timezone === 'string' && so.timezone.trim()
      ? so.timezone.trim()
      : DEFAULT_CLASS_TIMEZONE;

  let idPassthrough: string | undefined;
  if (so._id != null && so._id !== '') {
    const asString =
      typeof so._id === 'string'
        ? so._id
        : typeof (so._id as { toString?: () => string })?.toString ===
            'function'
          ? (so._id as { toString: () => string }).toString()
          : '';
    if (!asString || !isValidObjectId(asString)) {
      return {
        valid: false,
        error: `${fieldPrefix}._id must be a valid ObjectId when provided`,
      };
    }
    idPassthrough = asString.trim();
  }

  return {
    valid: true,
    data: {
      ...(idPassthrough !== undefined && { _id: idPassthrough }),
      date: so.date.trim(),
      timezone,
      notes,
      active: so.active === undefined ? true : Boolean(so.active),
    },
  };
}

export function validateSkippedOccurrences(
  skippedOccurrences: unknown
): ValidationResult<SkippedOccurrenceData[]> {
  if (!Array.isArray(skippedOccurrences)) {
    return {
      valid: false,
      error: 'skippedOccurrences must be an array',
    };
  }

  const normalized: SkippedOccurrenceData[] = [];
  for (let i = 0; i < skippedOccurrences.length; i += 1) {
    const result = validateSkippedOccurrence(skippedOccurrences[i], {
      index: i,
    });
    if (!result.valid) {
      return result;
    }
    normalized.push(result.data);
  }

  return {
    valid: true,
    data: normalized,
  };
}

export function sanitizeClassCodeAdminPayload(
  payload: unknown,
  options: SanitizePayloadOptions = {}
): ValidationResult<ClassCodeAdminPayloadData> {
  const mode = options.mode === 'update' ? 'update' : 'create';
  const now = isValidDate(options.now) ? options.now : new Date();

  if (
    payload == null ||
    typeof payload !== 'object' ||
    Array.isArray(payload)
  ) {
    return { valid: false, error: 'Request body is required' };
  }

  const p = payload as Record<string, unknown>;
  const data: ClassCodeAdminPayloadData = {};
  const isCreate = mode === 'create';
  const submittedPurposeRaw =
    p.purpose != null && String(p.purpose).trim()
      ? String(p.purpose).trim()
      : '';
  const existingPurposeRaw =
    !isCreate && options.existingPurpose != null
      ? String(options.existingPurpose).trim()
      : '';
  const purposeRaw = submittedPurposeRaw || existingPurposeRaw || 'live_class';
  if (!(CLASS_JOIN_CODE_PURPOSES as readonly string[]).includes(purposeRaw)) {
    return {
      valid: false,
      error: `purpose must be one of: ${CLASS_JOIN_CODE_PURPOSES.join(', ')}`,
    };
  }
  const purpose = purposeRaw as ClassJoinCodePurpose;
  const teachingFormat = hasOwn(p, 'teachingFormat')
    ? p.teachingFormat
    : (options.existingTeachingFormat ?? 'group');
  if (!(TEACHING_FORMATS as readonly unknown[]).includes(teachingFormat)) {
    return { valid: false, error: 'teachingFormat must be group or private' };
  }
  if (purpose === 'assignment_group' && teachingFormat === 'private') {
    return {
      valid: false,
      error: 'Assignment groups cannot be private classes',
    };
  }
  if (isCreate || hasOwn(p, 'teachingFormat')) {
    data.teachingFormat = teachingFormat as TeachingFormat;
  }
  if (isCreate || hasOwn(p, 'purpose')) {
    data.purpose = purpose;
  }

  const shouldHandleClassCode = isCreate || hasOwn(p, 'classCode');
  if (shouldHandleClassCode) {
    const classCodeRaw = p.classCode != null ? String(p.classCode).trim() : '';
    if (isCreate && !classCodeRaw) {
      return { valid: false, error: 'Class code is required' };
    }
    const normalizedCode = normalizeClassCode(classCodeRaw);
    if (!normalizedCode) {
      return { valid: false, error: 'Invalid class code format' };
    }
    data.classCodeNormalized = normalizedCode;
  }

  // Explicit null on update is handled (it clears the link) rather than
  // silently ignored: clearing is how a Personal-account class enters the
  // awaiting-teacher-link state after a placeholder cleanup.
  const shouldHandleZoomUrl = isCreate || hasOwn(p, 'zoomJoinUrl');
  if (shouldHandleZoomUrl) {
    const zoomJoinUrlRaw =
      p.zoomJoinUrl != null ? String(p.zoomJoinUrl).trim() : '';
    if (!zoomJoinUrlRaw && purpose === 'live_class') {
      // Personal-account classes may go linkless: the teacher adds their own
      // link and every surface renders the awaiting state until they do.
      // Shared/other accounts keep the hard requirement.
      const notesForZoomDecision = hasOwn(p, 'notes')
        ? (p.notes as string | null | undefined)
        : options.existingNotes;
      if (parseZoomAccount(notesForZoomDecision).kind !== 'personal') {
        return { valid: false, error: 'Zoom join URL is required' };
      }
    }
    if (zoomJoinUrlRaw && !isHttpsUrl(zoomJoinUrlRaw)) {
      return {
        valid: false,
        error: 'Zoom join URL must be a valid HTTPS URL',
      };
    }
    data.zoomJoinUrl = zoomJoinUrlRaw || null;
  }

  if (purpose === 'assignment_group') {
    data.joinEnabled = false;
    data.visibleInTeacherClassCodes = false;
  } else {
    if (isCreate || hasOwn(p, 'joinEnabled')) {
      data.joinEnabled = p.joinEnabled !== false;
    }
    if (isCreate || hasOwn(p, 'visibleInTeacherClassCodes')) {
      data.visibleInTeacherClassCodes = p.visibleInTeacherClassCodes !== false;
    }
  }

  const shouldHandleNotes = isCreate || hasOwn(p, 'notes');
  if (shouldHandleNotes) {
    if (p.notes == null) {
      data.notes = null;
    } else {
      const notes = String(p.notes).trim();
      if (notes.length > CLASS_NOTES_MAX_LENGTH) {
        return {
          valid: false,
          error: `Notes must be ${CLASS_NOTES_MAX_LENGTH} characters or less`,
        };
      }
      data.notes = notes || null;
    }
  }

  const shouldHandleTrialIntakeStatus =
    isCreate || hasOwn(p, 'trialIntakeStatus');
  if (shouldHandleTrialIntakeStatus) {
    const status =
      p.trialIntakeStatus != null && String(p.trialIntakeStatus).trim()
        ? String(p.trialIntakeStatus).trim()
        : 'accepting';
    if (!(TRIAL_INTAKE_STATUSES as readonly string[]).includes(status)) {
      return {
        valid: false,
        error: `trialIntakeStatus must be one of: ${TRIAL_INTAKE_STATUSES.join(', ')}`,
      };
    }
    data.trialIntakeStatus = status as TrialIntakeStatus;
  }

  const shouldHandleTrialIntakeNotes =
    isCreate || hasOwn(p, 'trialIntakeNotes');
  if (shouldHandleTrialIntakeNotes) {
    if (p.trialIntakeNotes == null) {
      data.trialIntakeNotes = null;
    } else {
      const notes = String(p.trialIntakeNotes).trim();
      if (notes.length > CLASS_TRIAL_INTAKE_NOTES_MAX_LENGTH) {
        return {
          valid: false,
          error: `trialIntakeNotes must be ${CLASS_TRIAL_INTAKE_NOTES_MAX_LENGTH} characters or less`,
        };
      }
      data.trialIntakeNotes = notes || null;
    }
  }

  const shouldHandleExpiresAt = isCreate || hasOwn(p, 'expiresAt');
  if (shouldHandleExpiresAt) {
    if (
      p.expiresAt === null ||
      p.expiresAt === '' ||
      p.expiresAt === undefined
    ) {
      data.expiresAt = null;
    } else {
      const expiresAt = parseDate(p.expiresAt);
      if (!expiresAt) {
        return {
          valid: false,
          error: 'Invalid expiresAt date format',
        };
      }
      data.expiresAt = expiresAt;
    }
  }

  const shouldHandleClassTiming = isCreate || hasOwn(p, 'classTiming');
  if (shouldHandleClassTiming) {
    if (p.classTiming === null || p.classTiming === '') {
      data.classTiming = null;
    } else if (p.classTiming !== undefined) {
      const classTimingValidation = validateClassTiming(p.classTiming, {
        now,
      });
      if (!classTimingValidation.valid) {
        return {
          valid: false,
          error: classTimingValidation.error,
        };
      }
      data.classTiming = classTimingValidation.data;
    } else if (isCreate) {
      data.classTiming = null;
    }
  }

  const shouldHandleSessionOverrides =
    isCreate || hasOwn(p, 'sessionOverrides');
  if (shouldHandleSessionOverrides) {
    if (
      p.sessionOverrides === null ||
      p.sessionOverrides === '' ||
      p.sessionOverrides === undefined
    ) {
      data.sessionOverrides = [];
    } else {
      const sessionOverridesValidation = validateSessionOverrides(
        p.sessionOverrides
      );
      if (!sessionOverridesValidation.valid) {
        return {
          valid: false,
          error: sessionOverridesValidation.error,
        };
      }
      data.sessionOverrides = sessionOverridesValidation.data;
    }
  }

  const shouldHandleSkippedOccurrences =
    isCreate || hasOwn(p, 'skippedOccurrences');
  if (shouldHandleSkippedOccurrences) {
    if (
      p.skippedOccurrences === null ||
      p.skippedOccurrences === '' ||
      p.skippedOccurrences === undefined
    ) {
      data.skippedOccurrences = [];
    } else {
      const skippedOccurrencesValidation = validateSkippedOccurrences(
        p.skippedOccurrences
      );
      if (!skippedOccurrencesValidation.valid) {
        return {
          valid: false,
          error: skippedOccurrencesValidation.error,
        };
      }
      data.skippedOccurrences = skippedOccurrencesValidation.data;
    }
  }

  const shouldHandleMainTeacher = isCreate || hasOwn(p, 'mainTeacherUserId');
  if (shouldHandleMainTeacher) {
    const raw = p.mainTeacherUserId;
    if (!raw) {
      data.mainTeacherUserId = null;
    } else if (!isValidObjectId(raw)) {
      return {
        valid: false,
        error: 'mainTeacherUserId must be a valid user ID',
      };
    } else {
      data.mainTeacherUserId = String(raw).trim();
    }
  }

  // Cohort (orgLevel + examLevel) is validated as a pair via the shared
  // guardrail so primary forces PSLE and secondary requires a valid exam
  // level. An empty/cleared orgLevel clears both. Handled whenever either
  // field is present (or always on create), mirroring the other optional
  // fields' hasOwn pattern.
  const shouldHandleLevels =
    isCreate || hasOwn(p, 'orgLevel') || hasOwn(p, 'examLevel');
  if (shouldHandleLevels) {
    if (p.orgLevel == null || String(p.orgLevel).trim() === '') {
      data.orgLevel = null;
      data.examLevel = null;
    } else {
      try {
        const resolved = validateAccessCodeLevels(
          p.orgLevel as string,
          p.examLevel as string | null | undefined
        );
        data.orgLevel = resolved.orgLevel;
        data.examLevel = resolved.examLevel;
      } catch (err) {
        return {
          valid: false,
          error: err instanceof Error ? err.message : 'Invalid level/stream',
        };
      }
    }
  }

  const shouldHandleMathSubjects = isCreate || hasOwn(p, 'mathSubjects');
  if (shouldHandleMathSubjects) {
    if (!Array.isArray(p.mathSubjects) || p.mathSubjects.length === 0) {
      data.mathSubjects = [];
    } else {
      const seen = new Set<string>();
      const subjects: string[] = [];
      for (const raw of p.mathSubjects) {
        const subject = typeof raw === 'string' ? raw.trim() : '';
        if (!(MATH_SUBJECTS as readonly string[]).includes(subject)) {
          return {
            valid: false,
            error: `mathSubjects must each be one of: ${MATH_SUBJECTS.join(', ')}`,
          };
        }
        if (!seen.has(subject)) {
          seen.add(subject);
          subjects.push(subject);
        }
      }
      data.mathSubjects = subjects;
    }
  }

  return { valid: true, data };
}
